/// <reference types="@crm/types" />
import React from 'react';
import ReactDOM from 'react-dom';
import DayjsUtils from '@date-io/dayjs';
import * as dayjs from 'dayjs';
import * as utc from 'dayjs/plugin/utc';
import fr from 'dayjs/locale/fr';
import * as isBetween from 'dayjs/plugin/isBetween';
import * as timezone from 'dayjs/plugin/timezone';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { createMuiTheme, CssBaseline, ThemeProvider } from '@material-ui/core';
import reportWebVitals from './reportWebVitals';
import { ProvideAuth } from './auth/auth.context';
import { ProvideAlerts } from './hooks/useAlerts';
import App from './App';
import './index.css';

/**
 * Configuration des plugins et locales Day.js
 * 
 * Cette section configure :
 * - Les plugins nécessaires pour la gestion des dates
 * - La locale française par défaut
 * - Le support des fuseaux horaires et UTC
 * - Les fonctions de comparaison de dates
 */
dayjs.extend(isBetween as any);
dayjs.extend(utc as any);
dayjs.extend(timezone as any);
dayjs.locale(fr);

/**
 * Thème Material-UI personnalisé pour l'application CRM
 * 
 * Ce thème définit :
 * - Les couleurs principales et secondaires
 * - Les couleurs de fond et de texte
 * - Les nuances de gris personnalisées
 * - Les couleurs d'erreur spécifiques
 * 
 * Logique métier :
 * - Couleur primaire : gris argenté (#C0C0C0)
 * - Couleur secondaire : violet (#6200EE)
 * - Fond par défaut : blanc (#fff)
 * - Texte principal : bleu foncé (#152738)
 */
const theme = createMuiTheme({
  palette: {
    background: {
      default: '#fff',
    },
    text: {
      primary: '#152738',
    },
    primary: {
      main: '#C0C0C0',
    },
    secondary: {
      main: '#6200EE',
    },
    error: {
      main: '#b44',
    },
    grey: {
      '300': '#E8EDF3',
      '400': '#d0d0d0',
      '500': '#a5a5a5',
    },
  },
});

/**
 * Point d'entrée principal de l'application React CRM
 * 
 * Cette fonction initialise et rend l'application avec :
 * - Configuration du thème Material-UI
 * - Providers pour l'authentification et les alertes
 * - Support des sélecteurs de date avec Day.js
 * - Mode strict React pour le développement
 * 
 * Logique métier :
 * - Structure hiérarchique des providers (Theme > Auth > Alerts > App)
 * - Configuration globale des styles et utilitaires
 * - Initialisation des performances web (reportWebVitals)
 */
ReactDOM.render(
  <React.StrictMode>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <MuiPickersUtilsProvider utils={DayjsUtils}>
        <ProvideAuth>
          <ProvideAlerts>
            <App />
          </ProvideAlerts>
        </ProvideAuth>
      </MuiPickersUtilsProvider>
    </ThemeProvider>
  </React.StrictMode>,
  document.getElementById('root'),
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
